/**
 * Sidebar Theme System
 *
 * Provides a centralized theme configuration for sidebar variants.
 * All colors are defined in main.css under "SIDEBAR PORTAL THEMES".
 *
 * To add a new portal theme:
 * 1. Add CSS variables in main.css (--sidebar-{variant}-*)
 * 2. Add the variant to SidebarVariant type
 * 3. Add theme mapping in SIDEBAR_THEMES
 */

/**
 * Available sidebar variants (portal types)
 */
export type SidebarVariant = "default" | "admin" | "hub";

/**
 * Theme configuration interface
 * All values are Tailwind CSS class strings referencing main.css variables
 */
export interface SidebarTheme {
  // Container styling
  background: string;
  border: string;

  // Text colors
  text: string;
  textSecondary: string;
  textMuted: string;

  // Interactive states
  hover: string;
  activeBg: string;
  activeBorder: string;

  // Navigation section
  sectionTitle: string;
  chevron: string;

  // User section
  userBorder: string;
  userBg: string;
  userBgHover: string;
  userBgActive: string;

  // Logo filter (for inverting on dark backgrounds)
  logoFilter: string;

  // Badge text (shown below logo)
  badge?: string;
  // Badge styling classes
  badgeStyle?: string;
}

/**
 * Theme configurations for each sidebar variant
 * All colors reference CSS variables from main.css
 */
export const SIDEBAR_THEMES: Record<SidebarVariant, SidebarTheme> = {
  default: {
    // References --sidebar-default-* from main.css
    background: "bg-[var(--sidebar-default-bg)]",
    border: "border-[var(--sidebar-default-border)]",
    text: "text-[var(--sidebar-default-text)]",
    textSecondary: "text-[var(--sidebar-default-text-secondary)]",
    textMuted: "text-[var(--sidebar-default-text-muted)]",
    hover: "hover:bg-[var(--sidebar-default-hover)]",
    activeBg: "bg-[var(--sidebar-default-active-bg)]",
    activeBorder: "border-[var(--sidebar-default-active-border)]",
    sectionTitle: "text-[var(--sidebar-default-section-title)]",
    chevron: "text-[var(--sidebar-default-chevron)]",
    userBorder: "border-[var(--sidebar-default-user-border)]",
    userBg: "bg-[var(--sidebar-default-user-bg)]",
    userBgHover: "hover:bg-[var(--sidebar-default-user-bg-hover)]",
    userBgActive: "bg-[var(--sidebar-default-user-bg-hover)]",
    logoFilter: "",
    badge: undefined,
    badgeStyle: undefined,
  },
  admin: {
    // References --sidebar-admin-* from main.css
    // Same neutral theme as default
    background: "bg-[var(--sidebar-admin-bg)]",
    border: "border-[var(--sidebar-admin-border)]",
    text: "text-[var(--sidebar-admin-text)]",
    textSecondary: "text-[var(--sidebar-admin-text-secondary)]",
    textMuted: "text-[var(--sidebar-admin-text-muted)]",
    hover: "hover:bg-[var(--sidebar-admin-hover)]",
    activeBg: "bg-[var(--sidebar-admin-active-bg)]",
    activeBorder: "border-[var(--sidebar-admin-active-border)]",
    sectionTitle: "text-[var(--sidebar-admin-section-title)]",
    chevron: "text-[var(--sidebar-admin-chevron)]",
    userBorder: "border-[var(--sidebar-admin-user-border)]",
    userBg: "bg-[var(--sidebar-admin-user-bg)]",
    userBgHover: "hover:bg-[var(--sidebar-admin-user-bg-hover)]",
    userBgActive: "bg-[var(--sidebar-admin-user-bg-hover)]",
    logoFilter: "",
    badge: "Admin",
    badgeStyle: "bg-border-secondary text-text-secondary",
  },
  hub: {
    // References --sidebar-hub-* from main.css
    // Same neutral theme as default (for investor portal)
    background: "bg-[var(--sidebar-hub-bg)]",
    border: "border-[var(--sidebar-hub-border)]",
    text: "text-[var(--sidebar-hub-text)]",
    textSecondary: "text-[var(--sidebar-hub-text-secondary)]",
    textMuted: "text-[var(--sidebar-hub-text-muted)]",
    hover: "hover:bg-[var(--sidebar-hub-hover)]",
    activeBg: "bg-[var(--sidebar-hub-active-bg)]",
    activeBorder: "border-[var(--sidebar-hub-active-border)]",
    sectionTitle: "text-[var(--sidebar-hub-section-title)]",
    chevron: "text-[var(--sidebar-hub-chevron)]",
    userBorder: "border-[var(--sidebar-hub-user-border)]",
    userBg: "bg-[var(--sidebar-hub-user-bg)]",
    userBgHover: "hover:bg-[var(--sidebar-hub-user-bg-hover)]",
    userBgActive: "bg-[var(--sidebar-hub-user-bg-hover)]",
    logoFilter: "",
    badge: "Hub",
    badgeStyle: "bg-border-secondary text-text-secondary",
  },
};

/**
 * Get theme configuration for a sidebar variant
 */
export const getSidebarTheme = (variant: SidebarVariant): SidebarTheme => {
  return SIDEBAR_THEMES[variant] || SIDEBAR_THEMES.default;
};

/**
 * Check if variant uses a dark background (for conditional styling)
 * Currently no variants use dark backgrounds - all use neutral zinc theme
 */
export const isDarkVariant = (variant: SidebarVariant): boolean => {
  // All variants currently use light backgrounds, parameter reserved for future use
  void variant;
  return false;
};
